import PadletApi from 'surface/padlet_api'
import uniqueId from 'lodash.uniqueid'
import debounce from 'lodash.debounce'
import { Commit, Dispatch } from 'vuex'
import { captureMessage } from 'bits/error_tracker'
import { FetchError } from 'surface/api_fetch'
import { Id, Cid, PostAttributes, Post } from 'types'

// HELPERS
const isNew = (post: Post | PostAttributes): boolean => !post.id
const isEmpty = (post: PostAttributes): boolean => (!post.subject && !post.body && !post.attachment)
const isNonEmpty = (post: PostAttributes): boolean => !isEmpty(post)

interface CommitAndDispatch {
  commit: Commit
  dispatch: Dispatch
}

interface CopyXferPostParams {
  wallId?: Id
  wallDescription?: string
  wallTitle?: string
  wallSectionId?: Id
  isDestinationThisWall?: boolean
  postPosition?: { left: number, top: number  }
  postId: Id,
  transferredPostCid?: Cid,
}

enum PostStatus {
  New,
  Creating,
  Created,
  DebouncingUpdate,
  Updating,
  UpdateWaiting,
}

const DEFAULT_RETRIES_LEFT = 1
interface PostState {
  status: PostStatus
  debouncedUpdate: null | (() => void) // lazily created
  retriesLeft: number
}

const PostStates: { [cid: string]: PostState } = {}
const getOrCreatePostState = (post: Post): PostState => {
  if (!PostStates[post.cid]) {
    PostStates[post.cid] = {
      status: isNew(post) ? PostStatus.New : PostStatus.Created,
      debouncedUpdate: null,
      retriesLeft: DEFAULT_RETRIES_LEFT,
    }
  }
  return PostStates[post.cid]
}

/*****************************************************

PostSaver manages saving of posts. It ensures that only one request
(either update or create) is being made at a time.

### USAGE

After instantiation, the only method meant to be called is `save`:

```
const saver = new PostSaver(post, dispatch)
saver.save() // create immediately if new
// ...
saver.save() // does not fire request if one is still in progress
// ...
saver.save() // updates if post has been persisted
saver.save() // debounces updates
```

### STATE DIAGRAM

Tip: start at the state 'NEW' and trace what happens when you invoke 'save'.
The other transitions happen automatically when requests are completed or debouncing ends.

+---------------+
|               |
|      NEW      |
|               |
+-----+-^-------+
      | |
      | |                                  save
 save | | create
      | | failed                          +-------+
      | |                                 |       |
+-----v-+-------+               +---------+----+  |
|               | create failed |              +<-+
|   CREATING    <---------------+    UPDATE    |
|               +--------------->    WAITING   |
|               |    save       |              +--+
+-------+-------+               +------+-------+  |
        |                              ^          |
        | create                  save |          |
        | done                         |          |
        |                              |          |
+-------v-------+               +------+-------+  |
|               | update done   |              |  |
|   CREATED     | update error  |   UPDATING   |  |
|               <---------------+              |  |
+-----+-+-------+               +------+-------+  |
      | ^                              ^          |
      | | (empty post)                 |          |
 save | |                       update |          |
      | |     +------------------------+          |
      | |     |                                   |
+-----v-+-----+-+                                 |
|               +<--------------------------------+
|   DEBOUNCING  |         create done
|   UPDATE      |         update done
|               |         update failed
+---+------+----+
    |      ^
    |      |
    +------+
      save

*****************************************************/

class PostSaver {
  private postCid!: Cid
  private postState!: PostState
  private dispatch!: Dispatch
  private getters!: any // Would need circular import if typed as Getter

  public constructor (postCid: Cid, { dispatch: PostModuleDispatch, getters: PostModuleGetters }) {
    this.postCid = postCid
    this.dispatch = PostModuleDispatch
    this.getters = PostModuleGetters
    this.postState = getOrCreatePostState(this.post)
  }

  public save (): void {
    if (isEmpty(this.post)) return
    switch (this.postState.status) {
      case PostStatus.New: { this.toCreating(); break }
      case PostStatus.Creating: { this.toUpdateWaiting(); break }
      case PostStatus.Created: { this.toDebouncingUpdate(); break }
      case PostStatus.DebouncingUpdate: { this.debouncedUpdate(); break }
      case PostStatus.Updating: { this.toUpdateWaiting(); break }
      case PostStatus.UpdateWaiting: // Do nothing
      default: break
    }
  }

  // STATE MUTATORS
  private toNew              (): void { this.postState.status = PostStatus.New } // a 'resting' state
  private toCreating         (): void { this.postState.status = PostStatus.Creating; this.create() }
  private toCreated          (): void { this.postState.status = PostStatus.Created } // a 'resting' state
  private toDebouncingUpdate (): void { this.postState.status = PostStatus.DebouncingUpdate; this.debouncedUpdate() }
  private toUpdating         (): void { this.postState.status = PostStatus.Updating; this.update() }
  private toUpdateWaiting    (): void { this.postState.status = PostStatus.UpdateWaiting }
  private resetRetries       (): void { this.postState.retriesLeft = DEFAULT_RETRIES_LEFT } // resetRetries at the end of all possible paths of each request
  private useRetry           (): void { this.postState.retriesLeft -= 1 }

  // Ensure that we always use the latest post data when creating/updating
  private get post (): Post {
    return this.getters.postEntitiesByCid[this.postCid]
  }

  private async create (): Promise<any> {
    try {
      const post = await PadletApi.Wish.create(this.post)
      this.createDone(post)
    } catch (e) {
      this.createFailed(e)
    }
  }

  private createDone (post: Post): void {
    const updateStore = (): Promise<any> => this.dispatch('createdPostSuccess', { ...post, ...this.post })

    switch (this.postState.status) {
      case PostStatus.Creating: { updateStore(); this.toCreated(); this.resetRetries(); break }
      case PostStatus.UpdateWaiting: { updateStore(); this.toDebouncingUpdate(); this.resetRetries(); break }
      default: this.handleInvalidState('createDone')
    }
  }

  private createFailed (e): void {
    const reportError = (): void => {
      const context = { response: e.message, attachment: this.post.attachment }
      captureMessage(`CreatedPostError ${e.status || ''}`, { context })
    }
    const retryOrQuit = (): void => {
      if (e.status < 500 || this.postState.retriesLeft < 1) {
        reportError()
        this.resetRetries()
      } else {
        this.useRetry()
        setTimeout((): void => this.save(), 500)
      }
    }

    if (e.status === 403) this.dispatch('showReloadNotification', null, { root: true }) // Reload if no permissions
    if (e.status >= 400 && e.status < 500) return

    switch (this.postState.status) {
      case PostStatus.Creating: { this.toNew(); retryOrQuit(); break }
      case PostStatus.UpdateWaiting: { this.resetRetries(); this.toCreating(); break }
      default: this.handleInvalidState('createFailed')
    }
  }

  private async debouncedUpdate (): Promise<any> {
    if (!this.postState.debouncedUpdate) {
      this.postState.debouncedUpdate = debounce((): void => {
        const post = this.post
        // The post might have been deleted while debouncing
        if (!post) return
        // User might have emptied post while debouncing
        isEmpty(post) ? this.toCreated() : this.toUpdating()
      }, 500)
    }
    this.postState.debouncedUpdate()
  }

  private async update (): Promise<any> {
    try {
      const post = await PadletApi.Wish.update(this.post)
      this.updateDone(post)
    } catch (e) {
      this.updateFailed(e)
    }
  }

  private updateDone (post: Post): void {
    const updateStore = (): Promise<any> => this.dispatch('updatedPostSuccess', { ...post, ...this.post })

    switch (this.postState.status) {
      case PostStatus.Updating: { updateStore(); this.toCreated(); this.resetRetries(); break }
      case PostStatus.UpdateWaiting: { updateStore(); this.toDebouncingUpdate(); this.resetRetries(); break }
      default: this.handleInvalidState('updateDone')
    }
  }

  private updateFailed (e): void {
    const reportError = (): void => {
      const context = { response: e.message, postId: this.post.id }
      captureMessage(`UpdatedPostError ${e.status || ''}`, { context })
    }
    const retryOrQuit = (): void => {
      if (e.status < 500 || this.postState.retriesLeft < 1) {
        reportError()
        this.resetRetries()
      } else {
        this.useRetry()
        this.save()
      }
    }

    if (e.status === 404) this.dispatch('refresh', null, { root: true }) // Refresh if post deleted
    if (e.status === 403) this.dispatch('showReloadNotification', null, { root: true }) // Reload if no permissions
    if (e.status >= 400 && e.status < 500) return

    switch (this.postState.status) {
      case PostStatus.UpdateWaiting: { this.resetRetries(); this.toDebouncingUpdate(); break }
      case PostStatus.Updating: { this.toCreated(); retryOrQuit(); break }
      default: this.handleInvalidState('updateFailed')
    }
  }

  private handleInvalidState (methodName: string): void {
    const context = { methodName, stateReached: PostStatus[this.postState.status] }
    captureMessage('PostSaver invalid state reached', { context })
  }
}

const savePost = (context, post: Post | PostAttributes): void => {
  const { commit, getters } = context
  // Ensures that: (1) post is added to store, (2) post had cid
  commit(isNew(post) ? 'CREATED_POST' : 'UPDATED_POST', post)
  const cid = post.cid || getters.getPostByServerId(post.id).cid
  const saver = new PostSaver(cid, context)
  saver.save()
}

/**
 * Delete post using PadletApi abstraction
 */
async function deletePost ({ commit, dispatch }: CommitAndDispatch, postToBeSaved: Post): Promise<boolean> {
  try {
    commit('DELETED_POST', postToBeSaved)
    const obj = await PadletApi.Wish.delete({
      id: postToBeSaved.id,
    })
    commit('DELETED_POST_SUCCESS', { ...obj, cid: postToBeSaved.cid })
    return true
  } catch (e) {
    if (e.status === 404) {
      dispatch('refresh', null, { root: true })
      return true
    }
    if (e.status === 403) {
      dispatch('showReloadNotification', null, { root: true })
      return false
    }
    commit('DELETED_POST_ERROR', postToBeSaved)
    const context = {
      response: e.message,
      postId: postToBeSaved.id,
    }
    captureMessage(`DeletedPostError ${e.status || ''}`, { context })
    return false
  }
}

/**
 * Delete all posts using PadletApi abstraction
 */
function deleteAllPosts ({ commit }: {commit: Commit}, posts: Post[]): void {
  posts.forEach((post): void => {
    PadletApi.Wish.delete({
      id: post.id,
    }).then((obj: Post): void => {
      commit('DELETED_POST', post)
      commit('DELETED_POST_SUCCESS', { ...obj, cid: post.cid })
    }).catch((e: FetchError): void => {
      commit('DELETED_POST_ERROR', post)
      const context = {
        response: e.message,
        action: 'deleteAllPosts',
      }
      captureMessage(`DeletedPostError ${e.status || ''}`, { context })
    })
  })
}

async function copyPost ({ dispatch }, params: CopyXferPostParams): Promise<void> {
  const copyFunc = PadletApi.Wish.copy.bind(PadletApi.Wish)
  const { wallId, wallDescription, wallTitle, wallSectionId, postPosition } = params
  try {
    const postAttributes = await copyFunc({
      wishId: params.postId,
      wallId,
      wallDescription,
      wallTitle,
      wallSectionId,
      postPosition,
    })
    const isDestinationNewWall = !!wallTitle || !!wallDescription
    dispatch('postAction/updatePostCopied', {
      newPostData: postAttributes,
      originalPostId: params.postId,
      isDestinationNewWall,
    }, { root: true })
  } catch (error) {
    // contents of error
    // {
    //    "response": { /* response object */ },
    //    "status": 422, /* some 4xx error */
    //    "message": "{ \"error\": \"some error message\" }"
    // }
    // check to see if "message" can be JSON decoded
    let parsedError = error
    try {
      parsedError = JSON.parse(error.message)
    } finally {
      dispatch('postAction/updatePostCopied', { error: parsedError }, { root: true })
    }
  }
}

async function transferPost ({ dispatch }, params: CopyXferPostParams): Promise<void> {
  const { wallId, wallDescription, wallTitle, wallSectionId, isDestinationThisWall, transferredPostCid } = params
  const transferFunc = PadletApi.Wish.transfer.bind(PadletApi.Wish)
  try {
    // can transfer intra-wall, between shelf columns
    const postAttributes = await transferFunc({
      wishId: params.postId,
      wallId,
      wallDescription,
      wallTitle,
      wallSectionId,
      // postPosition,
    })
    const isDestinationNewWall = !!wallTitle || !!wallDescription
    dispatch('postAction/updatePostTransferred', {
      newPostData: postAttributes,
      originalPostId: params.postId,
      isDestinationThisWall,
      isDestinationNewWall,
      transferredPostCid,
    }, { root: true })
  } catch (error) {
    // contents of error
    // {
    //    "response": { /* response object */ },
    //    "status": 422, /* some 4xx error */
    //    "message": "{ \"error\": \"some error message\" }"
    // }
    // check to see if "message" can be JSON decoded
    let parsedError = error
    try {
      parsedError = JSON.parse(error.message)
    } finally {
      dispatch('postAction/updatePostTransferred', { error: parsedError }, { root: true })
    }
  }
}

/**
 * Get the next temp client ID for the next new post being added to
 * client-side list of posts.
 */
function nextTempClientId (): string {
  return uniqueId('c_new')
}

function generateClientId (post: PostAttributes): string {
  if (post.id) {
    return 'c' + post.id
  }
  return nextTempClientId()
}

function getClientId (post: Post): string|null {
  if (post.cid) {
    return post.cid
  }
  if (post.id) {
    return 'c' + post.id
  }
  return null
}
function isValidClientId (cid: Cid): boolean {
  if (!String(cid).startsWith('c')) {
    return false
  }
  return true
}

export {
  deletePost,
  deleteAllPosts,
  copyPost,
  transferPost,
  savePost,
  generateClientId,
  getClientId,
  isValidClientId,
  isNew,
  isEmpty,
  isNonEmpty,
}
